<template>
  <AdminLayout>
    <template #breadcrumb>
      <breadcrumb-link
        view="AdminAlertPlanNameList"
        label="Alert Plan Names"
        :active="true"
      />
    </template>

    <div class="box" style="position: relative">
      <b-loading :is-full-page="false" v-model="loading"></b-loading>
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <div>
              <h1 class="title mb-2">Manage Alert Plan Names</h1>
              <p class="subtitle is-6 has-text-grey mt-0">Total: {{ totalCount }} Plan Names</p>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <b-button type="is-success" @click="openCreateModal">
              <b-icon icon="plus"></b-icon>
              <span>Create New</span>
            </b-button>
          </div>
        </div>
      </div>
      <hr />

      <!-- Search -->
      <b-field>
        <div class="field-body">
          <b-field label="Search PlanName">
            <b-input
              v-model="search"
              placeholder="Search by PlanName..."
              icon="magnify"
              @input="debouncedSearch"
            ></b-input>
          </b-field>
        </div>
      </b-field>

      <div class="table-container">
        <b-table
          :data="PlanName"
          :loading="loading"
          hoverable
          striped
          :paginated="true"
          backend-pagination
          :total="totalCount"
          :per-page="perPage"
          :current-page="currentPage"
          @page-change="onPageChange"
          pagination-position="bottom"
          default-sort="PlanName"
          default-sort-direction="asc"
        >
          <b-table-column field="PlanName" label="Plan Name" v-slot="props" sortable>
            <span class="has-text-weight-semibold">{{ props.row.plan_name }}</span>
          </b-table-column>

          <b-table-column label="Actions" width="160" v-slot="props">
            <div class="field is-grouped">
              <div class="control">
                <b-button
                  type="is-primary is-outlined"
                  size="is-small"
                  icon-left="pencil"
                  @click="openEditModal(props.row)"
                >
                  Edit
                </b-button>
              </div>
              <div class="control">
                <b-button
                  type="is-danger"
                  size="is-small"
                  icon-left="delete"
                  @click="confirmDelete(props.row)"
                >
                  Delete
                </b-button>
              </div>
            </div>
          </b-table-column>

          <template #empty>
            <div class="has-text-centered">
              <b-icon icon="emoticon-sad" size="is-large"></b-icon>
              <p>No PlanName found.</p>
            </div>
          </template>
        </b-table>
      </div>

      <!-- Create Modal -->
      <b-modal v-model="isCreateModalActive" :width="500" scroll="keep">
        <form @submit.prevent="createPlanNames">
          <div class="modal-card">
            <header class="modal-card-head">
              <p class="modal-card-title">Create New Plan Name</p>
            </header>
            <section class="modal-card-body">
              <b-loading :is-full-page="false" v-model="isCreating"></b-loading>

              <b-field
                label="Plan Name"
                required
                :type="{ 'is-danger': !!createErrors.plan_name }"
                :message="createErrors.plan_name"
              >
                <b-input
                  v-model="createForm.plan_name"
                  placeholder="Enter Plan Name"
                  required
                  maxlength="255"
                ></b-input>
              </b-field>
            </section>
            <footer class="modal-card-foot">
              <b-button @click="closeCreateModal">Cancel</b-button>
              <b-button type="is-success" native-type="submit" :loading="isCreating">
                Create
              </b-button>
            </footer>
          </div>
        </form>
      </b-modal>

      <!-- Edit Modal -->
      <b-modal v-model="showEditModal" :width="500" scroll="keep">
        <form @submit.prevent="handleUpdatePlanName">
          <div class="modal-card" style="width: auto">
            <header class="modal-card-head">
              <p class="modal-card-title">Edit planName</p>
              <button type="button" class="delete" @click="showEditModal = false"></button>
            </header>
            <section class="modal-card-body">
              <b-loading :is-full-page="false" v-model="editLoading"></b-loading>

              <b-field label="Plan Name" :type="{ 'is-danger': !!errors.plan_name }" :message="errors.plan_name">
                <b-input
                  v-model="editForm.plan_name"
                  placeholder="Enter Plan Name"
                  required
                  maxlength="255"
                ></b-input>
              </b-field>
            </section>
            <footer class="modal-card-foot">
              <b-button @click="showEditModal = false">Cancel</b-button>
              <b-button type="is-primary" native-type="submit" :loading="editLoading">
                Save Changes
              </b-button>
            </footer>
          </div>
        </form>
      </b-modal>
    </div>
  </AdminLayout>
</template>

<script>
import request from '../../request';
import AdminLayout from '../../layouts/AdminLayout.vue';
export default {
  name: 'AdminAlertPlanNameList',
  components: {
    AdminLayout,
  },
  data() {
    return {
      loading: false,
      PlanName: [],
      totalCount: 0,
      currentPage: 1,
      perPage: 50,
      search: '',
      searchTimeout: null,
      isCreateModalActive: false,
      createForm: {
        plan_name: '',
      },
      createErrors: {},
      isCreating: false,
      showEditModal: false,
      editLoading: false,
      editingplanName: null,
      editForm: {},
      errors: {},
    };
  },
  created() {
    this.loadPlanNames();
  },
  methods: {
    async loadPlanNames() {
      this.loading = true;
      try {
        const params = new URLSearchParams();
        if (this.perPage) params.append('limit', this.perPage);
        if (this.currentPage > 1) params.append('page', this.currentPage);
        if (this.search.trim()) params.append('term', this.search.trim());
        const response = await request.get(`/admin/alerts/plan-names?${params.toString()}`);
        this.PlanName = response.data.plan_names || [];
        this.totalCount = parseInt(response.headers['x-total-count'] || '0');
      } catch (error) {
        console.error('Error loading PlanName:', error);
        this.$buefy.toast.open({
          message: 'Failed to load PlanName',
          type: 'is-danger',
        });
      } finally {
        this.loading = false;
      }
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadPlanNames();
    },
    debouncedSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadPlanNames();
      }, 300);
    },
    openCreateModal() {
      this.createForm = {
        plan_name: '',
      };
      this.createErrors = {};
      this.isCreateModalActive = true;
    },
    closeCreateModal() {
      this.isCreateModalActive = false;
      this.createForm = {
        plan_name: '',
      };
      this.createErrors = {};
    },
    async createPlanNames() {
      this.isCreating = true;
      this.createErrors = {};
      try {
        const payload = {
          plan_name: this.createForm.plan_name?.trim(),
        };
        await request.post('/admin/alerts/plan-names', payload);
        this.$buefy.toast.open({
          message: 'Plan Name created successfully',
          type: 'is-success',
        });
        this.closeCreateModal();
        this.loadPlanNames();
      } catch (error) {
        console.error('Error creating plan name:', error);
        if (error.response?.data?.errors) {
          this.createErrors = error.response.data.errors;
        } else {
          const msg = error.response?.data?.code == 422 ? 'plan name already exists' : 'Failed to create plan name';
          this.$buefy.toast.open({
            message: msg,
            type: 'is-danger',
          });
        }
      } finally {
        this.isCreating = false;
      }
    },
    openEditModal(obj) {
      this.editingplanName = obj;
      this.editForm = {
        plan_name: obj.plan_name,
      };
      this.errors = {};
      this.showEditModal = true;
    },
    async handleUpdatePlanName() {
      this.editLoading = true;
      this.errors = {};
      try {
        const payload = {
          plan_name: this.editForm.plan_name?.trim(),
        };
        await request.put(`/admin/alerts/plan-names/${this.editingplanName.id}`, payload);
        this.$buefy.toast.open({
          message: 'Plan Name updated successfully',
          type: 'is-success',
        });
        this.showEditModal = false;
        this.loadPlanNames();
      } catch (error) {
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else if (error.response?.data?.message) {
          let msg = error.response?.data?.code == 422 ? 'plan name already exists' : 'Failed to update plan name';
          this.$buefy.toast.open({
            message: msg,
            type: 'is-danger',
          });
        } else {
          this.$buefy.toast.open({
            message: 'Failed to update planName',
            type: 'is-danger',
          });
        }
      } finally {
        this.editLoading = false;
      }
    },
    confirmDelete(obj) {
      this.$buefy.dialog.confirm({
        title: 'Delete Plan Name',
        message: `Are you sure you want to delete "<strong>${obj.plan_name}</strong>"? This action cannot be undone.`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.deletePlanName(obj),
      });
    },
    async deletePlanName(obj) {
      try {
        await request.delete(`/admin/alerts/plan-names/${obj.id}`);
        this.$buefy.toast.open({
          message: 'Plan Name deleted successfully',
          type: 'is-success',
        });
        this.loadPlanNames();
      } catch (error) {
        console.error('Error deleting plan name:', error);
        let errorMessage = 'Failed to delete plan name';
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.status === 400) {
          errorMessage = 'Cannot delete this plan name - it may still be in use by alerts';
        }
        // Show error message in a dialog for better visibility
        this.$buefy.dialog.alert({
          title: 'Delete Failed',
          message: errorMessage,
          type: 'is-danger',
          hasIcon: true,
          ariaRole: 'alertdialog',
          ariaModal: true,
        });
      }
    },
  },
};
</script>
